import type { CSSProperties } from 'react';
import { ORIGINALS, type OriginalTheme } from './catalog.js';
import { pieceUrl } from './assets/sprites.js';

const THEMES = new Map(ORIGINALS.map((o) => [o.code, o.theme]));
const PLATES = new Set(ORIGINALS.filter((o) => o.art?.plate).map((o) => o.code));

/** Whether the game's stage is painted with its drawn backdrop plate (globals.css `[data-og-plate]`). */
export function hasStagePlate(code: string): boolean {
  return PLATES.has(code);
}

export function originalTheme(code: string): OriginalTheme | null {
  return THEMES.get(code) ?? null;
}

/**
 * The game's stage colours as CSS variables for the element that wraps its page: `--og-*`
 * feed the stage backdrop and its plate (globals.css). The controls are not the game's: the
 * bet button, the risk switch and the panels keep the site's own theme (`--primary` and the
 * surfaces the tenant's branding sets), whichever game they sit in.
 */
export function originalThemeStyle(code: string): CSSProperties | undefined {
  const theme = originalTheme(code);
  if (!theme) return undefined;
  return {
    '--og-hue': theme.hue,
    '--og-glow': theme.glow,
    '--og-deep': theme.deep,
    '--og-panel': theme.panel,
    ...(PLATES.has(code)
      ? { '--og-plate': `image-set(url("${pieceUrl(code, 'stage', 1)}") 1x, url("${pieceUrl(code, 'stage', 2)}") 2x)` }
      : {}),
  } as CSSProperties;
}

/** A `#rrggbb` theme colour as the number Pixi takes. */
export function sceneColor(hex: string): number {
  return parseInt(hex.replace(/^#/, ''), 16);
}

/**
 * Re-colours a payout ring by tier: every slice of the lowest multiplier takes
 * `tiers[0]`, the next multiplier `tiers[1]`, and so on, so the wheel and its
 * legend agree on one colour per multiplier. Without tiers, or with more
 * multipliers than tiers, the server's colour stands.
 */
export function tierColors<T extends { multiplierBp: number; color: string }>(
  ring: T[],
  tiers: string[] | undefined,
): T[] {
  if (!tiers || tiers.length === 0) return ring;
  const ranked = [...new Set(ring.map((s) => s.multiplierBp))].sort((a, b) => a - b);
  if (ranked.length > tiers.length) return ring;
  return ring.map((s) => ({ ...s, color: tiers[ranked.indexOf(s.multiplierBp)]! }));
}

/** The game's badge fill: its hue sinking into its deep base, like the tile. */
export function originalThemeGradient(code: string): string | null {
  const theme = originalTheme(code);
  return theme ? `linear-gradient(150deg, ${theme.hue}, ${theme.deep})` : null;
}

/** `color` moved `t` (0..1) of the way toward `toward`, channel by channel. */
export function mixColor(color: number, toward: number, t: number): number {
  const ch = (c: number, shift: number) => (c >> shift) & 0xff;
  const mix = (shift: number) => Math.round(ch(color, shift) + (ch(toward, shift) - ch(color, shift)) * t);
  return (mix(16) << 16) | (mix(8) << 8) | mix(0);
}
